
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight,
  ChevronLeft, 
  ChevronRight,
  Briefcase, 
  TrendingUp, 
  CheckCircle, 
  Clock, 
  Shield, 
  Building2,
  Sparkles,
  ArrowLeftRight
} from 'lucide-react';
import { useIsMobile } from "@/hooks/use-mobile";
import Image from "@/components/ui/image";
import yrityslainatPromoImage from "@/assets/yrityslainat-promo.jpg";

interface YrityslainatPromotionProps {
  variant?: 'default' | 'compact' | 'inline';
  className?: string;
}

export const YrityslainatPromotion: React.FC<YrityslainatPromotionProps> = ({ variant = 'default', className = '' }) => {
  const isMobile = useIsMobile();
  
  // Compact variant - used in sidebar and grid layouts
  if (variant === 'compact') {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
        className={className}
      >
        <Card className="border border-blue-100 overflow-hidden shadow-md hover:shadow-xl transition-all h-full relative">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-3">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-medium flex items-center text-sm sm:text-base">
                <Sparkles className="h-4 w-4 text-yellow-200 mr-2" />
                <span>Yrityslainat</span>
              </h3>
            </div>
          </div>
          
          <CardContent className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-50/50 flex flex-col h-full">
            <div className="mb-3 flex-grow">
              <p className="text-xs sm:text-sm text-gray-700">
                Rahoitusratkaisut yrityksesi tarpeisiin. Kilpailuta ja vertaile, säästät rahaa ja aikaa!
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-3">
              <div className="flex items-center text-xs bg-blue-100 py-0.5 px-2 rounded-full text-blue-700">
                <Briefcase className="h-3 w-3 mr-1" />
                <span>1000€-3M€</span>
              </div>
              <div className="flex items-center text-xs bg-blue-100 py-0.5 px-2 rounded-full text-blue-700">
                <Clock className="h-3 w-3 mr-1" />
                <span>1-120kk</span>
              </div>
              <div className="flex items-center text-xs bg-green-100 py-0.5 px-2 rounded-full text-green-700">
                <TrendingUp className="h-3 w-3 mr-1" />
                <span>Kasvu</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between gap-2">
              <Link to="/lainat/kulutusluotto" className="flex-shrink-0">
                <Button variant="outline" size="sm" className="h-8 px-2">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </Link>
              
              <Link to="/lainat/yrityslaina" className="flex-grow">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm hover:shadow text-xs sm:text-sm py-2 h-auto">
                  Katso tarjoukset <ArrowRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </Link>
              
              <Link to="/lainat/pienlainat-luottokortit" className="flex-shrink-0">
                <Button variant="outline" size="sm" className="h-8 px-2">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  if (variant === 'inline') {
    return (
      <motion.div 
        initial={{ opacity: 0.9, y: 5 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`flex items-center bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border border-blue-100 shadow-sm overflow-hidden ${className}`}
      >
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-3 px-4 hidden sm:flex items-center justify-center">
          <Briefcase className="h-6 w-6 text-white" />
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-grow p-3 sm:py-2 sm:px-4 gap-2 sm:gap-4">
          <div className="flex items-center">
            <Briefcase className="h-4 w-4 text-blue-600 flex-shrink-0 sm:hidden mr-2" />
            <div>
              <p className="font-medium text-blue-700 text-sm">Tarvitsetko rahoitusta yrityksellesi?</p>
              <p className="text-xs text-blue-600">Vertaile yrityslainoja</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Link to="/lainat/kulutusluotto" className="sm:hidden">
              <Button variant="outline" size="sm" className="h-8 px-2">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </Link>
            
            <Link to="/lainat/yrityslaina" className="w-full sm:w-auto">
              <Button size="sm" className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs">
                Katso tarjoukset <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
            
            <Link to="/lainat/pienlainat-luottokortit" className="sm:hidden">
              <Button variant="outline" size="sm" className="h-8 px-2">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default variant with more content and enhanced visuals
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <Card className="border border-blue-200 overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 h-full">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img 
              src={yrityslainatPromoImage} 
              alt="Yrityslainat ja rahoitus" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mt-10 -mr-10 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -mb-10 -ml-10 blur-xl"></div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold">Yrityslainat</h3>
              <p className="text-blue-100 mt-1 flex items-center text-sm sm:text-base">
                <TrendingUp className="h-4 w-4 mr-1" />
                Rahoitusratkaisut yritystoimintaan
              </p>
            </div>
            <motion.div 
              whileHover={{ rotate: 10, scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="hidden md:flex items-center justify-center bg-white/20 h-16 w-16 rounded-full"
            >
              <Building2 className="h-8 w-8 text-white" />
            </motion.div>
          </div>
        </div>
        
        <CardContent className="p-4 sm:p-5 bg-gradient-to-br from-white to-blue-50/50 flex flex-col h-full">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 sm:mb-5">
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-blue-100 shadow-sm"
            >
              <div className="bg-blue-100 p-2 rounded-full">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-700 text-sm">Käyttöpääoma</h4>
                <p className="text-xs text-gray-600">1000€-3M€</p>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-blue-100 shadow-sm"
            >
              <div className="bg-blue-100 p-2 rounded-full">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-700 text-sm">Investoinnit</h4>
                <p className="text-xs text-gray-600">Yrityksen kasvu</p>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-green-100 shadow-sm"
            >
              <div className="bg-green-100 p-2 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Edullisin</h4>
                <p className="text-xs text-gray-600">Vertaile ehtoja</p>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
            <div className="flex items-center text-xs sm:text-sm bg-blue-50 py-1 px-3 rounded-full text-blue-700 border border-blue-100">
              <Clock className="h-3.5 w-3.5 mr-1" />
              <span>Nopea päätös</span>
            </div>
            <div className="flex items-center text-xs sm:text-sm bg-green-50 py-1 px-3 rounded-full text-green-700 border border-green-100">
              <Shield className="h-3.5 w-3.5 mr-1" />
              <span>Turvallinen prosessi</span>
            </div>
            <div className="flex items-center text-xs sm:text-sm bg-blue-50 py-1 px-3 rounded-full text-blue-700 border border-blue-100">
              <TrendingUp className="h-3.5 w-3.5 mr-1" />
              <span>Useita tarjouksia</span>
            </div>
          </div>
          
          <div className="mt-auto pt-2 space-y-3">
            <Link to="/lainat/yrityslaina">
              <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2 sm:py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-auto text-sm sm:text-base">
                Vertaile yrityslainoja <ArrowRight className="ml-1 sm:ml-2 h-4 w-4 animate-pulse" />
              </Button>
            </Link>
            
            <div className="flex items-center justify-between gap-2">
              <Link to="/lainat/kulutusluotto" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Kulutusluotot
                </Button>
              </Link>
              
              <Link to="/lainat/pienlainat-luottokortit" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">
                  Pienlainat <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="text-center mt-2">
              <Link to="/kilpailuta" className="text-blue-600 hover:text-blue-800 text-xs">
                <ArrowLeftRight className="inline h-3 w-3 mr-1" /> Vertaile kaikkia lainoja
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
